import dayjs from "dayjs";
import { COMMON_FIELD_TYPES } from "../constants/fieldTypes";
import { errorMessages } from "../constants/frontendErrorMessages";
import { sanitizeHyphens } from "./formatting";

// NOTE: Ghana Phone Number Pattern
const GHANA_PHONE_NUMBER_PATTERN = /^(?:\+233|0)[2356789]\d{8}$/;

// Validate MongoDB ObjectId format
export const isValidObjectId = (id) => /^[a-fA-F0-9]{24}$/.test(id);

// Validate email
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return errorMessages.INVALID_EMAIL;
  return "";
};

// Validate multiple emails (comma-separated)
export const validateMultipleEmails = (emails) => {
  if (!emails || typeof emails !== "string") return "";

  const emailList = emails.split(",").map((email) => email.trim());

  for (const email of emailList) {
    if (email && validateEmail(email) !== "") {
      return "Please enter valid email addresses (comma separated for multiple)";
    }
  }

  return "";
};

// FUNCTION: Validate Phone Number
function validatePhone(phoneNumber) {
  if (!phoneNumber) {
    return "";
  }

  const cleanPhone = phoneNumber.trim().replace(/[\s\-\(\)\.]/g, "");

  if (!/^\+?[\d]+$/.test(cleanPhone)) {
    return errorMessages.INVALID_PHONE_NUMBER;
  }

  const digitsOnly = cleanPhone.replace(/^\+/, "");

  if (digitsOnly.length < 7) {
    return errorMessages.INVALID_PHONE_NUMBER;
  } else if (digitsOnly.length > 15) {
    return errorMessages.INVALID_PHONE_NUMBER;
  }

  return "";
}

// Validate email or phone number
const validateEmailOrPhone = (input) => {
  if (!input) {
    return errorMessages.REQUIRED_FIELD;
  } else if (validateEmail(input) === "") {
    // valid email
    return "";
  } else if (validatePhone(input) === "") {
    // valid phone number
    return "";
  }

  return errorMessages.INVALID_EMAIL_OR_PHONE; // invalid email or phone number
};

// Validate password
const validatePassword = (password) => {
  const hasMinLength = password.length >= 6;

  if (!hasMinLength) {
    return errorMessages.INVALID_PASSWORD;
  }

  return "";
};

// FUNCTION: Validate number
const isValidNumber = (input) => {
  // Check if the input is of type "number" or can be converted to a valid number
  if (typeof input === "number" && !isNaN(input)) {
    return "";
  }
  if (
    typeof input === "string" &&
    !isNaN(parseFloat(input)) &&
    isFinite(input)
  ) {
    return "";
  }
  return errorMessages.INVALID_NUMBER;
};

// FUNCTION: Validate date
const isValidDate = (dateString) => {
  console.log("dateString", dateString);

  // If no date provided
  if (!dateString) return false;

  // If it's a dayjs object, format it first
  if (dayjs.isDayjs(dateString)) {
    dateString = dateString.format("YYYY-MM-DD");
    console.log("dayjs formatted date", dateString);
  }

  // Check format (YYYY-MM-DD)
  const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateFormatRegex.test(dateString)) return false;

  // Parse the date parts
  const [year, month, day] = dateString.split("-").map(Number);

  // Create a new Date object and verify the values
  const date = new Date(year, month - 1, day); // month is 0-based in JavaScript
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set time to start of the day

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    year >= 1900 && // Reasonable minimum year
    year <= 2100 && // Reasonable maximum year
    date >= today // Ensure the date is not in the past
  );
};
// USAGE:
// EXAMPLE:
/*
  console.log(isValidDate('2025-01-25')); // true
  console.log(isValidDate('2025-13-25')); // false (invalid month)
  console.log(isValidDate('2025-02-30')); // false (invalid day)
  console.log(isValidDate('202/02/20'));  // false (wrong format)
  console.log(isValidDate(''));           // false
  console.log(isValidDate(null));         // false
  console.log(isValidDate(dayjs()));      // true (current date)
*/

// FUNCTION: Validate time
const isValidTime = (time) => {
  const timeRegex = /^(0[1-9]|1[0-2]):([0-5][0-9])\s(AM|PM)$/;
  return timeRegex.test(time);
};

// FUNCTION: Ghana Nic Number Validation - Format Should be like this GHA-XXXXXXXXX-X
function isValidGhanaIdNumber(pin) {
  // INFO:
  //  A three-letter country code (e.g., 'GHA' for Ghana),
  //  Followed by a hyphen,
  //  Nine digits,
  //  Another hyphen,
  //  And a single check digit.

  // // USAGE: Regular expression to match the Ghana Card PIN format
  // const pinRegex = /^GHA-\d{9}-\d$/;
  // return pinRegex.test(pin);

  // Check if the input is a string and has a minimum length of 1
  if (typeof pin !== "string" || pin.length < 1) {
    return false;
  }
}
// EXAMPLE:
// console.log(validateGhanaCardPIN("GHA-123456789-0")); // Output: true
// console.log(validateGhanaCardPIN("GHA-12345-0")); // Output: false

// FUNCTION: Validate field
export const validateField = (name, type, value, formData, requiredFields) => {
  if (!type) return "Error";

  let error = "";
  const fieldType = typeof type === "object" ? type.type : type; // Extract actual type

  switch (fieldType) {
    case COMMON_FIELD_TYPES.REQUIRED_FIELD:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      break;

    case COMMON_FIELD_TYPES.BOOLEAN_FIELD:
      if (!value && value !== false && requiredFields[name])
        error = errorMessages.REQUIRED_FIELD;
      else if (
        !(!value && value !== false) &&
        !(value === false || value === true)
      )
        error = errorMessages.INVALID_OPTION;
      break;

    case COMMON_FIELD_TYPES.NUMBER_FIELD:
      if (!value && value !== false && requiredFields[name])
        error = errorMessages.REQUIRED_FIELD;
      else if (value) error = isValidNumber(value);
      break;

    case COMMON_FIELD_TYPES.FIRST_NAME:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      break;

    case COMMON_FIELD_TYPES.LAST_NAME:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      break;

    case COMMON_FIELD_TYPES.EMAIL_FIELD:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value) error = validateEmail(value);
      break;

    case COMMON_FIELD_TYPES.PHONE:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value) error = validatePhone(value);
      break;

    case COMMON_FIELD_TYPES.PASSWORD_FIELD:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value) error = validatePassword(value);
      break;

    case COMMON_FIELD_TYPES.CONFIRM_PASSWORD:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value !== formData.password)
        error = errorMessages.PASSWORD_MISMATCH;
      break;

    case COMMON_FIELD_TYPES.DROPDOWN:
      console.log("dropdown", value, requiredFields[name]);
      if ((!value || Object.keys(value).length === 0) && requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
      }
      //   else if (typeof value !== "object" || !value.hasOwnProperty("id"))
      //     error = errorMessages.INVALID_OPTION;
      break;

    case COMMON_FIELD_TYPES.DATE_PICKER:
      if (!value && requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
      } else if (value && !isValidDate(value)) {
        console.log("trying to validate date", value);
        error = errorMessages.INVALID_DATE;
      }
      break;

    case COMMON_FIELD_TYPES.TIME_PICKER:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (!isValidTime(value)) error = errorMessages.INVALID_TIME;
      break;

    case COMMON_FIELD_TYPES.ID_NUMBER:
      if (!value && requiredFields[name]) {
        console.log("ID Number is required");
        error = errorMessages.REQUIRED_FIELD;
      } else if (isValidGhanaIdNumber(value)) {
        error = errorMessages.INVALID_ID_NUMBER;
      }
      break;

    case COMMON_FIELD_TYPES.EMAIL_OR_PHONE:
      if (!value && requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
      } else if (value) {
        error = validateEmailOrPhone(value);
      }
      break;

    case COMMON_FIELD_TYPES.POSITIVE_DECIMAL:
      if (!value && requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
      } else if (value) {
        const decimalRegex = /^\d+(\.\d{1,2})?$/;
        if (!decimalRegex.test(value)) {
          error = errorMessages.INVALID_POSITIVE_DECIMAL;
        }
      }
      break;

    default:
      break;
  }

  return error;
};

// Check if tag is not empty
export function isNotEmpty(value) {
  if (typeof value === "string") {
    return value.trim() !== "";
  }
  return value !== null && value !== undefined;
}

// Check if value has min and max characters
export function hasLengthBetween(value, min = 0, max = Infinity) {
  const length = value.trim().length;
  return length >= min && length <= max;
}

// Check if tag contains only ASCII (no emojis or special Unicode)
export function isAsciiOnly(value) {
  const asciiRegex = /^[\x00-\x7F\u2019]*$/;
  return asciiRegex.test(value);
}

export function isEmojiFree(value) {
  // Covers most emoji ranges (Unicode blocks for symbols and pictographs)
  const emojiRegex =
    /[\u{1F300}-\u{1F6FF}]|[\u{1F700}-\u{1F77F}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA70}-\u{1FAFF}]|[\u2600-\u26FF]|[\u2700-\u27BF]/u;
  return !emojiRegex.test(value);
}

// Check if tag contains only letters, numbers, hyphens, and spaces
export function isAlphabetsNumbersSpaces(value) {
  const pattern = /^[A-Za-z0-9\s\-]+$/;
  return pattern.test(value);
}

export function isAlphabetsNumbersSpacesTags(value) {
  const pattern = /^[A-Za-z0-9\s\-&]+$/;
  return pattern.test(value);
}

// Check hyphen placements
export function isValidHyphen(value) {
  if (typeof value !== "string") return false;

  // Disallow leading/trailing hyphens
  if (/^\s*-|-\s*$/.test(value)) return false;

  // Disallow consecutive hyphens
  if (/--/.test(value)) return false;

  // Disallow patterns like "word- word" or "word -word"
  if (/\b-\s|\s-\b/.test(value)) return false;

  return true;
}

export function containsAtLeastOneLetter(value) {
  const regex = /[A-Za-z]/;
  return regex.test(value);
}

export function isAlphabetsAndNumbersOnly(value) {
  const pattern = /^[A-Za-z0-9]+$/;
  return pattern.test(value);
}

export function isAlphabetsNumbersSpacesOnly(value) {
  return /^[A-Za-z0-9 ]+$/.test(value);
}

export function validateTag(value) {
  value = sanitizeHyphens(value);
  if (!isNotEmpty(value)) return "Tag name is required";
  if (!hasLengthBetween(value, 1, 30))
    return "Tag name must not exceed 30 characters";
  if (!isAsciiOnly(value))
    return "No emojis or special Unicode symbols allowed";
  if (!isAlphabetsNumbersSpacesTags(value))
    return "Only letters, numbers, spaces, and hyphens are allowed";
  if (!isValidHyphen(value)) return "Invalid hyphen placement.";

  return "";
}

export function validateArtistName(value) {
  if (!isNotEmpty(value)) return "Artist name is required";
  if (!hasLengthBetween(value, 2, 50))
    return "Must be between 2 and 50 characters";
  if (!isEmojiFree(value)) return "Emojis are not allowed";
  return "";
}

export function validateRemixerName(value) {
  if (!isNotEmpty(value)) return "Remixer name is required";
  if (!hasLengthBetween(value, 1, 50))
    return "Must be under 50 characters";
  if (!isEmojiFree(value)) return "Emojis are not allowed";
  if (!containsAtLeastOneLetter(value))
    return "Invalid Format for Remixer Name";
  return "";
}

export function validateCouponCode(value) {
  if (!isNotEmpty(value)) return "Coupon Code is required";
  if (!hasLengthBetween(value, 1, 20)) return "Must be less than 20 characters";
  if (!isAlphabetsAndNumbersOnly(value)) return "No Spaces, symbols, emojis";
  return "";
}

export function validateDiscountType(value) {
  if (!isNotEmpty(value)) return "Discount Type is required";
  return "";
}

export function validatePlanType(value) {
  if (!isNotEmpty(value)) return "Plan Type is required";
  return "";
}

export function validateInfluencer(value) {
  if (!isNotEmpty(value)) return "Influencer is required";
  return "";
}

export function validateSubcategory(value) {
  value = sanitizeHyphens(value);
  if (!isNotEmpty(value)) return "Subcategory name is required";
  if (!hasLengthBetween(value, 1, 50))
    return "Subcategory name must not exceed 50 characters";
  if (!isEmojiFree(value)) return "Emojis are not allowed";
  if (!isAsciiOnly(value)) return "No special Unicode symbols allowed";
  return "";
}

export function validateProducerCategory(value) {
  value = sanitizeHyphens(value);
  if (!isNotEmpty(value)) return "Producer category name is required";
  if (!hasLengthBetween(value, 1, 50))
    return "Producer category name must not exceed 50 characters";
  if (!isEmojiFree(value)) return "Emojis are not allowed";
  if (!isAsciiOnly(value)) return "No special Unicode symbols allowed";
  return "";
}

export function validatePlaylistTitle(value) {
  value = sanitizeHyphens(value);
  if (!isNotEmpty(value)) return "Playlist title is required";
  if (!hasLengthBetween(value, 2, 100))
    return "Playlist title must be 2 to 100 characters";
  if (!isEmojiFree(value)) return "Emojis are not allowed";
  if (!isAsciiOnly(value)) return "No special Unicode symbols allowed";
  return "";
}


// Charts: Validate timespan input (allowed: 7, 30, 90, 365)
export function validateChartTimespan(value) {
  const trimmed = String(value ?? "").trim();
  if (trimmed === "") return "Timespan is required";
  const num = Number(trimmed);
  if (!Number.isInteger(num)) return "Timespan must be a whole number";
  const allowed = [7, 30, 90, 365];
  if (!allowed.includes(num)) return "Allowed values: 7, 30, 90, 365";
  return "";
}

// Charts: Validate rank input (1-50, whole number)
export function validateChartRank(value) {
  const trimmed = String(value ?? "").trim();
  if (trimmed === "") return "Rank is required";
  const num = Number(trimmed);
  if (!Number.isInteger(num)) return "Rank must be a whole number";
  if (num < 1 || num > 50) return "Rank must be between 1 and 50";
  return "";
}


export const validateDiscountAmount = (value) => {
  if (!value) {
    return "Discount amount is required.";
  }

  const numValue = parseFloat(value);

  if (isNaN(numValue)) {
    return "Please enter a valid number.";
  }

  if (numValue <= 0) {
    return "Discount amount must be greater than 0.";
  }

  if (numValue > 10000) {
    return "Discount amount cannot exceed $10,000.";
  }

  // Check for too many decimal places
  const decimalPlaces = (value.split('.')[1] || '').length;
  if (decimalPlaces > 2) {
    return "Amount can have maximum 2 decimal places.";
  }

  return ""; // No error
};


/* -------------------- Admin coupen Validators -------------------- */
export const validateAdminCouponCode = (raw) => {
    const v = (raw || "").trim();
    if (!v) return "Coupon code is required";
    if (v.length < 2 || v.length > 50)
        return "Coupon code must be between 2 and 50 characters";
    // Allow A–Z, 0–9, dash/underscore (adjust as you like)
    if (!/^[A-Za-z0-9_-]+$/.test(v))
        return "Only letters, numbers, '-' and '_' are allowed";
    return null; // valid
};

export const validateDesc = (raw, label = "Description") => {
    const v = (raw || "").trim();
    if (!v) return `${label} is required`;
    if (v.length > 2000) return `${label} must be 2000 characters or less`;
    return null;
};

/* ==================================================== */

// Custom validation for To field - accepts "all" or valid email addresses
export const validateToField = (value) => {
  if (!value || !value.trim())
    return "Please enter valid email addresses (comma separated for multiple) or 'all'";

  const trimmedValue = value.trim().toLowerCase();

  // Accept "all" for sending to all users
  if (trimmedValue === "all") return "";

  // Otherwise validate as email addresses
  return validateMultipleEmails(value);
};